import { Clock } from 'lucide-react';
import { cn } from '../lib/cn';
import { Badge } from './badge';

export interface FreshnessProps {
  /** Optional query state; timestamp-only callers keep their existing behavior. */
  state?: 'loading' | 'error' | 'empty' | 'ready';
  /**
   * ISO timestamp of the last successful refresh. Pass `null` /
   * `undefined` when nothing has ever synced.
   */
  timestamp?: string | null;
  /**
   * Hours after which the indicator renders a "Stale" badge. Default 24.
   *
   * Pass `null` to show the timestamp WITHOUT asserting staleness at all. That is not the same as
   * a very large threshold: some sources legitimately go quiet while perfectly healthy — a job
   * queue whose items are all waiting on long delays writes nothing for hours — and any hour-scale
   * number would raise a stale warning on a working system. A badge that cries wolf costs the same
   * as one that stays silent when something is wrong: both teach the reader to ignore it. Where the
   * available data cannot support a health claim, make no claim.
   */
  staleAfterHours?: number | null;
  /**
   * If true, renders a "Provisional" badge. Caller decides when this
   * is appropriate (e.g. Google Ads data within its 14-day conversion
   * settlement window).
   */
  provisional?: boolean;
  className?: string;
}

/**
 * Reusable "Data as of …" pill. Matches the shape of the Marketing
 * module's inline FreshnessBadge so any module can adopt it without
 * reimplementing the clock-icon + relative-time dance.
 */
export function Freshness({
  state,
  timestamp,
  staleAfterHours = 24,
  provisional = false,
  className,
}: FreshnessProps) {
  if (state === 'loading') return null;

  if (state === 'error' || state === 'empty' || !timestamp) {
    return (
      <span
        className={cn('inline-flex items-center gap-1.5 text-caption text-text-muted', className)}
      >
        <Clock className="h-3 w-3" aria-hidden />
        {state === 'error' ? 'Freshness unavailable' : 'No sync yet'}
      </span>
    );
  }

  const ts = new Date(timestamp).getTime();
  const hoursAgo = Number.isNaN(ts) ? Infinity : (Date.now() - ts) / (1000 * 60 * 60);
  // null disables the claim entirely. Omitting the prop does NOT do this — it falls back to 24h,
  // which is how a caller that meant "do not assert staleness" still shipped a stale badge.
  const isStale = staleAfterHours !== null && hoursAgo >= staleAfterHours;

  return (
    <span
      className={cn('inline-flex items-center gap-1.5 text-caption text-text-secondary', className)}
    >
      <Clock className="h-3 w-3" aria-hidden />
      Data as of {formatRelative(timestamp)}
      {isStale ? <Badge variant="warning">Stale</Badge> : null}
      {provisional ? <Badge variant="purple">Provisional</Badge> : null}
    </span>
  );
}

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return iso;
  const diffSec = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} d ago`;
}
